import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Switch } from "../../components/ui/switch"
import MarkdownEditor from '@uiw/react-markdown-editor'
import '@uiw/react-markdown-editor/markdown-editor.css'
import { useTheme } from "../../layouts/ThemeProvider"

interface BlogPostFormProps {
  data: {
    title: string
    excerpt: string
    content: string
    tags_string: string
    slug: string
    published: boolean
    featured: boolean
  }
  setData: (field: string, value: any) => void
  onSubmit: (e: React.FormEvent) => void
  onCancel?: () => void
  submitLabel: string
  processing: boolean
  errors: any
}

const BlogPostForm = ({ 
  data,
  setData,
  onSubmit, 
  onCancel, 
  submitLabel, 
  processing, 
  errors 
}: BlogPostFormProps) => {
  const { theme } = useTheme()

  // Generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
  }

  const handleTitleChange = (value: string) => {
    setData('title', value)
    // Auto-generate slug if it's empty or matches the old title
    if (!data.slug || data.slug === generateSlug(data.title)) {
      setData('slug', generateSlug(value))
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6" name="blog_post">
      <div className="space-y-2">
        <Label htmlFor="title">Title *</Label>
        <Input
          id="title"
          name="blog_post[title]"
          value={data.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Enter blog post title"
          className={errors.title ? 'border-red-500' : ''}
          required
        />
        {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          name="blog_post[slug]"
          value={data.slug}
          onChange={(e) => setData('slug', e.target.value)}
          placeholder="blog-post-url-slug"
          className={errors.slug ? 'border-red-500' : ''}
        />
        <p className="text-xs text-muted-foreground">
          URL-friendly version of the title. Auto-generated from title, but can be customized.
        </p>
        {errors.slug && <p className="text-sm text-red-500">{errors.slug}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="excerpt">Excerpt</Label>
        <Input
          id="excerpt"
          name="blog_post[excerpt]"
          value={data.excerpt}
          onChange={(e) => setData('excerpt', e.target.value)}
          placeholder="Enter a brief excerpt"
          className={errors.excerpt ? 'border-red-500' : ''}
        />
        {errors.excerpt && <p className="text-sm text-red-500">{errors.excerpt}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Content * (Markdown Editor)</Label>
        <div className={errors.content ? 'border border-red-500 rounded-md' : ''}>
          <MarkdownEditor
            value={data.content}
            onChange={(value) => setData('content', value)}
            height="400px"
            visible={true}
            enablePreview={true}
            previewWidth="100%"
            previewProps={{
              wrapperElement: {
                "data-color-mode": theme === "system" ? "light" : theme
              }
            }}
            style={{
              border: 'none',
              borderRadius: '0.375rem'
            }}
          />
        </div>
        {errors.content && <p className="text-sm text-red-500">{errors.content}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags_string">Tags (comma separated)</Label>
        <Input
          id="tags_string"
          name="blog_post[tags_string]"
          value={data.tags_string}
          onChange={(e) => setData('tags_string', e.target.value)}
          placeholder="rails, web development, tutorials"
          className={errors.tags_string ? 'border-red-500' : ''}
        />
        {errors.tags_string && <p className="text-sm text-red-500">{errors.tags_string}</p>}
      </div>

      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="published"
            name="blog_post[published]"
            checked={data.published}
            onCheckedChange={(checked) => setData('published', checked)}
          />
          <Label htmlFor="published">Published</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="featured"
            name="blog_post[featured]"
            checked={data.featured}
            onCheckedChange={(checked) => setData('featured', checked)}
          />
          <Label htmlFor="featured">Featured</Label>
        </div>
      </div>

      <div className="flex gap-3">
        <Button type="submit" disabled={processing}>
          {processing ? 'Saving...' : submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}

export default BlogPostForm 